/**
 * agentVisuals — palette + icon library specific to agent identity.
 *
 * Decoupled from nav-tab category colors (the prior bug: a Sales agent
 * looked like the Marketplace tab because both shared "marketplace"
 * category gradient). Now agent appearance is independent of the
 * platform chrome.
 *
 * Research-grounded:
 *   - Color palette: 32 vibrant jewel-tone gradients (highlight + base
 *     + shadow per entry). Inspired by 3D character render aesthetic
 *     (saturated tones + specular highlights). Larger than Linear's
 *     ~20 / Notion's ~10 because agents far outnumber projects in our
 *     domain — workspaces with 40-60 agents need more identity room.
 *   - Icon library: 150 curated Lucide glyphs (~1500 total in lucide-
 *     react, ~29M weekly downloads, default in shadcn/ui). Organized
 *     into 14 categories surfaced as section headers in the picker.
 *   - Fallback: Boring Avatars `marble` seeded from agent.id, palette
 *     drawn from the 12 anchor accents. Feels enterprise/abstract,
 *     never "cartoon at work."
 *
 * Persistence: agent.metadata_json.icon = { color, iconName }
 *   color    — one of AGENT_PALETTE keys (e.g. "indigo")
 *   iconName — one of AGENT_ICON_LIBRARY keys (e.g. "briefcase")
 * Both fields are optional. When unset, the marble fallback renders
 * with initials overlay. AvatarPicker writes both at once.
 */

import {
  // Communication / support
  Headphones, MessageCircle, Megaphone, Send, Mail, Mic, MessageSquare,
  Phone, PhoneCall, MailQuestion, Voicemail, Inbox, AtSign, Volume2,
  // Knowledge / docs / law
  BookOpen, ScrollText, Scale, Gavel, FileSearch, Library, Archive,
  FileText, Notebook, BookMarked, Files, FolderOpen, FilePlus, Bookmark,
  // Healthcare
  Stethoscope, Pill, HeartPulse, Microscope, Syringe, Thermometer,
  Brain, Dna, Activity, Bandage,
  // Finance / commerce
  Briefcase, BadgeDollarSign, TrendingUp, Receipt, CreditCard, Banknote,
  Coins, Calculator, Wallet, PiggyBank, LineChart, BadgePercent,
  // Engineering / tech
  Code2, Wrench, Cpu, Server, Bug, Network, Terminal, GitBranch,
  Database, Cloud, Key, Shield, Container, Settings, Hammer,
  // Operations / ops
  Workflow, Truck, Boxes, Package, ClipboardCheck, MapPin, Route,
  Factory, Fuel, Gauge, Forklift, Anchor,
  // Sales / CRM
  Target, Handshake, UserCheck, Gift, Presentation, BarChart3,
  ShoppingCart, ShoppingBag, Tag, Percent, Star,
  // HR / people
  Users, UserCog, GraduationCap, Award, UserPlus, Contact, IdCard,
  Smile, HeartHandshake, BadgeCheck, UsersRound, UserSearch,
  // Project management
  Kanban, ListChecks, CalendarClock, Flag, Milestone, Timer, Hourglass,
  CalendarDays, CheckCircle2, ListTodo, Target as TargetPM, GanttChart,
  // Data / analytics
  PieChart, Sparkles, Search, Filter, Table, Telescope,
  ScanText, FileBarChart, Layers as LayersData, FunctionSquare,
  // AI / automation
  Bot, Zap, Wand2, Atom, Cog, Lightbulb, Flame, Rocket, Orbit, Infinity as InfinityIcon,
  // Geography / nature
  Compass, Globe, Mountain, TreePine, Sunrise, Moon, Cloud as CloudG,
  // Misc
  Crown, Diamond, Gem, Trophy, Shapes, Palette, Paintbrush, Music,
  Camera, Video, Image as ImageIcon, Film, Eye, Coffee, Pizza,
  Utensils, Plane, Car, Bike,
} from "lucide-react";

// ─── Palette ─────────────────────────────────────────────────────


// Vibrant 3D-orb palette. Each entry carries a four-stop gradient set:
//   highlight — bright top-left specular ("light catching the orb")
//   base      — mid-tone main hue
//   shadow    — deep ambient occlusion at the bottom-right
//   soft      — back-compat field consumed by older callers; equal to
//               highlight here so anything reading `.soft` still gets
//               a sensible light backdrop.
//
// Inspiration: 3D character render aesthetic (saturated jewel tones +
// strong specular highlight + tinted shadow). Renders as a sphere/orb
// in AgentAvatar via a radial gradient + curved gloss + tinted halo.
//
// Keys are stable across the palette redesign so existing
// metadata_json.icon.color values keep resolving. The first 12 keys
// (tomato … slate) are the anchor set; the 20 following expand the
// space for orgs with many agents.
export const AGENT_PALETTE = {
  // — Reds / corals —
  tomato:    { name: "Coral",     highlight: "#FFC4B8", base: "#FF5A4F", shadow: "#7F1A14", soft: "#FFE6E0" },
  ruby:      { name: "Ruby",      highlight: "#FFB1B1", base: "#E11D48", shadow: "#5A0E22", soft: "#FFD7DC" },
  crimson:   { name: "Crimson",   highlight: "#FF9494", base: "#DC2626", shadow: "#5C0F0F", soft: "#FFD0CE" },
  // — Oranges —
  orange:    { name: "Sunset",    highlight: "#FFD08A", base: "#F97316", shadow: "#7A2E0B", soft: "#FFE0CC" },
  tangerine: { name: "Tangerine", highlight: "#FFC07A", base: "#FB923C", shadow: "#7C2D12", soft: "#FFE2BC" },
  apricot:   { name: "Apricot",   highlight: "#FFD9A8", base: "#FFA94D", shadow: "#8A4012", soft: "#FFEACE" },
  // — Yellows —
  amber:     { name: "Citrus",    highlight: "#FFEC8C", base: "#F59E0B", shadow: "#78350F", soft: "#FFF1C4" },
  honey:     { name: "Honey",     highlight: "#FFE08A", base: "#EAB308", shadow: "#6F4D08", soft: "#FFF1B5" },
  sunshine:  { name: "Sunshine",  highlight: "#FFF59D", base: "#FCD34D", shadow: "#7A5A0F", soft: "#FFF7C0" },
  // — Greens —
  grass:     { name: "Lime",      highlight: "#BDF374", base: "#65D43A", shadow: "#1F5612", soft: "#E2F8CB" },
  emerald:   { name: "Emerald",   highlight: "#7BE8B8", base: "#10B981", shadow: "#064E3B", soft: "#C8F2DD" },
  jade:      { name: "Jade",      highlight: "#86EFAC", base: "#22C55E", shadow: "#14532D", soft: "#D1F5DB" },
  forest:    { name: "Forest",    highlight: "#84D9A1", base: "#16A34A", shadow: "#0E3A1F", soft: "#C9F0D5" },
  // — Cyans / teals —
  teal:      { name: "Mint",      highlight: "#7DF0CC", base: "#14B8A6", shadow: "#0B4F47", soft: "#C8F2DD" },
  cyan:      { name: "Lagoon",    highlight: "#A1E8FF", base: "#06B6D4", shadow: "#0E4A5C", soft: "#C7E5EE" },
  turquoise: { name: "Turquoise", highlight: "#9BE7E7", base: "#0EA5A5", shadow: "#0A4848", soft: "#C2EAEA" },
  // — Blues —
  sky:       { name: "Sky",       highlight: "#9EDDFF", base: "#0EA5E9", shadow: "#0C4A6E", soft: "#CBE8FB" },
  blue:      { name: "Ocean",     highlight: "#9DC8FF", base: "#3B82F6", shadow: "#162E66", soft: "#CCE3FF" },
  sapphire:  { name: "Sapphire",  highlight: "#9CB5FF", base: "#2563EB", shadow: "#172554", soft: "#CFDBFF" },
  royal:     { name: "Royal",     highlight: "#B0B5FF", base: "#4F46E5", shadow: "#231A6B", soft: "#D2D5FF" },
  // — Indigos / violets —
  indigo:    { name: "Twilight",  highlight: "#B5BBFF", base: "#6366F1", shadow: "#2D2A75", soft: "#D2DEFF" },
  violet:    { name: "Cosmic",    highlight: "#D4B0FF", base: "#A855F7", shadow: "#4A1574", soft: "#DCD1FF" },
  lavender:  { name: "Lavender",  highlight: "#DDD6FE", base: "#8B5CF6", shadow: "#3B1A78", soft: "#E5DDFE" },
  plum:      { name: "Plum",      highlight: "#E2C7FF", base: "#9333EA", shadow: "#421777", soft: "#E8D6FF" },
  // — Pinks / magentas —
  purple:    { name: "Aurora",    highlight: "#FFAEEB", base: "#D946EF", shadow: "#5C0F6B", soft: "#F1D5F8" },
  fuchsia:   { name: "Fuchsia",   highlight: "#FDA4DD", base: "#E879F9", shadow: "#581C87", soft: "#F3CCEB" },
  pink:      { name: "Candy",     highlight: "#FFB7DE", base: "#EC4899", shadow: "#6F1A48", soft: "#F8CDE2" },
  rose:      { name: "Rose",      highlight: "#FFC4D6", base: "#F43F5E", shadow: "#6F1029", soft: "#FFD9E2" },
  blush:     { name: "Blush",     highlight: "#FFD4DC", base: "#FB7185", shadow: "#7A1D2E", soft: "#FFE3E8" },
  // — Neutrals —
  slate:     { name: "Graphite",  highlight: "#D5DCE3", base: "#64748B", shadow: "#1E2733", soft: "#DDE2E6" },
  carbon:    { name: "Carbon",    highlight: "#C9CDD3", base: "#475569", shadow: "#111827", soft: "#D6DAE0" },
  pearl:     { name: "Pearl",     highlight: "#F1F5F9", base: "#94A3B8", shadow: "#3F4754", soft: "#E2E8F0" },
};

export const AGENT_PALETTE_KEYS = Object.keys(AGENT_PALETTE);

// Pull the seeded color list for Boring Avatars marble. Marble takes
// 5 colors and blends them with deterministic SVG paths.
export const AGENT_PALETTE_HEXES = AGENT_PALETTE_KEYS.map(k => AGENT_PALETTE[k].base);


// ─── Icon library ────────────────────────────────────────────────


// 150 curated Lucide glyphs grouped into 14 categories. The picker
// surfaces them in this order (with category section headers); the
// search input drives fuzzy match across `name` + `keywords`.
//
// Order within each category: most-likely → less-likely so the picker
// reads naturally without scrolling for typical agent personas.
export const AGENT_ICON_LIBRARY = [
  // — Communication / support (14)
  { name: "headphones",   Icon: Headphones,    category: "Communication", keywords: "support call agent voice" },
  { name: "chat",         Icon: MessageCircle, category: "Communication", keywords: "chat message conversation" },
  { name: "message-sq",   Icon: MessageSquare, category: "Communication", keywords: "message reply" },
  { name: "phone",        Icon: PhoneCall,     category: "Communication", keywords: "phone call outbound" },
  { name: "phone-base",   Icon: Phone,         category: "Communication", keywords: "phone telephone" },
  { name: "mic",          Icon: Mic,           category: "Communication", keywords: "voice mic dictation" },
  { name: "voicemail",    Icon: Voicemail,     category: "Communication", keywords: "voicemail audio msg" },
  { name: "volume",       Icon: Volume2,       category: "Communication", keywords: "audio sound speaker" },
  { name: "megaphone",    Icon: Megaphone,     category: "Communication", keywords: "marketing announce campaign" },
  { name: "mail",         Icon: Mail,          category: "Communication", keywords: "email letter inbox" },
  { name: "mail-q",       Icon: MailQuestion,  category: "Communication", keywords: "email support inquiry" },
  { name: "inbox",        Icon: Inbox,         category: "Communication", keywords: "inbox tickets queue" },
  { name: "send",         Icon: Send,          category: "Communication", keywords: "send outreach message" },
  { name: "at",           Icon: AtSign,        category: "Communication", keywords: "mention email handle" },

  // — Knowledge / docs / law (14)
  { name: "book",         Icon: BookOpen,      category: "Knowledge", keywords: "knowledge docs reference" },
  { name: "library",      Icon: Library,       category: "Knowledge", keywords: "library archive corpus" },
  { name: "scroll",       Icon: ScrollText,    category: "Knowledge", keywords: "docs document policy" },
  { name: "scale",        Icon: Scale,         category: "Knowledge", keywords: "legal compliance balance" },
  { name: "gavel",        Icon: Gavel,         category: "Knowledge", keywords: "legal court ruling judgment" },
  { name: "search-file",  Icon: FileSearch,    category: "Knowledge", keywords: "audit research investigate" },
  { name: "file-text",    Icon: FileText,      category: "Knowledge", keywords: "document text article" },
  { name: "files",        Icon: Files,         category: "Knowledge", keywords: "documents collection" },
  { name: "folder-open",  Icon: FolderOpen,    category: "Knowledge", keywords: "folder repo files" },
  { name: "file-plus",    Icon: FilePlus,      category: "Knowledge", keywords: "create draft new doc" },
  { name: "notebook",     Icon: Notebook,      category: "Knowledge", keywords: "notes memo journal" },
  { name: "book-marked",  Icon: BookMarked,    category: "Knowledge", keywords: "saved bookmark catalog" },
  { name: "bookmark",     Icon: Bookmark,      category: "Knowledge", keywords: "bookmark save reference" },
  { name: "archive",      Icon: Archive,       category: "Knowledge", keywords: "archive history compliance" },

  // — Healthcare (10)
  { name: "stethoscope",  Icon: Stethoscope,   category: "Healthcare", keywords: "healthcare doctor medical" },
  { name: "pill",         Icon: Pill,          category: "Healthcare", keywords: "pharma medication drug" },
  { name: "heart-pulse",  Icon: HeartPulse,    category: "Healthcare", keywords: "vitals patient monitor" },
  { name: "microscope",   Icon: Microscope,    category: "Healthcare", keywords: "research lab analysis" },
  { name: "syringe",      Icon: Syringe,       category: "Healthcare", keywords: "vaccine injection" },
  { name: "thermometer",  Icon: Thermometer,   category: "Healthcare", keywords: "temperature fever vitals" },
  { name: "dna",          Icon: Dna,           category: "Healthcare", keywords: "genetics biology research" },
  { name: "activity",     Icon: Activity,      category: "Healthcare", keywords: "vitals heartbeat ecg" },
  { name: "bandage",      Icon: Bandage,       category: "Healthcare", keywords: "first aid wound care" },
  { name: "brain",        Icon: Brain,         category: "Healthcare", keywords: "neurology cognition mind" },

  // — Finance / commerce (12)
  { name: "briefcase",    Icon: Briefcase,     category: "Finance", keywords: "business work executive" },
  { name: "dollar",       Icon: BadgeDollarSign, category: "Finance", keywords: "money pricing finance" },
  { name: "trending-up",  Icon: TrendingUp,    category: "Finance", keywords: "growth analytics forecast" },
  { name: "line-chart",   Icon: LineChart,     category: "Finance", keywords: "trend chart timeseries" },
  { name: "bar-chart",    Icon: BarChart3,     category: "Finance", keywords: "analytics report metrics" },
  { name: "receipt",      Icon: Receipt,       category: "Finance", keywords: "invoice billing accounting" },
  { name: "card",         Icon: CreditCard,    category: "Finance", keywords: "payment billing card" },
  { name: "banknote",     Icon: Banknote,      category: "Finance", keywords: "cash money currency" },
  { name: "coins",        Icon: Coins,         category: "Finance", keywords: "coins money pricing" },
  { name: "calculator",   Icon: Calculator,    category: "Finance", keywords: "math accounting compute" },
  { name: "wallet",       Icon: Wallet,        category: "Finance", keywords: "wallet balance account" },
  { name: "piggy",        Icon: PiggyBank,     category: "Finance", keywords: "savings deposit vault" },
  { name: "percent",      Icon: BadgePercent,  category: "Finance", keywords: "discount rate offer" },

  // — Engineering / tech (15)
  { name: "code",         Icon: Code2,         category: "Tech", keywords: "engineering devops code" },
  { name: "terminal",     Icon: Terminal,      category: "Tech", keywords: "shell devops cli sre" },
  { name: "git-branch",   Icon: GitBranch,     category: "Tech", keywords: "git repo branch source" },
  { name: "wrench",       Icon: Wrench,        category: "Tech", keywords: "ops maintenance tool" },
  { name: "hammer",       Icon: Hammer,        category: "Tech", keywords: "build maker construction" },
  { name: "settings",     Icon: Settings,      category: "Tech", keywords: "configuration tooling cog" },
  { name: "cpu",          Icon: Cpu,           category: "Tech", keywords: "compute hardware chip" },
  { name: "server",       Icon: Server,        category: "Tech", keywords: "infra ops backend" },
  { name: "container",    Icon: Container,     category: "Tech", keywords: "docker kubernetes runtime" },
  { name: "cloud",        Icon: Cloud,         category: "Tech", keywords: "cloud aws gcp azure" },
  { name: "database",     Icon: Database,      category: "Tech", keywords: "data sql storage" },
  { name: "network",      Icon: Network,       category: "Tech", keywords: "telecom networking" },
  { name: "bug",          Icon: Bug,           category: "Tech", keywords: "issue bug-tracking qa" },
  { name: "key",          Icon: Key,           category: "Tech", keywords: "security secret credential" },
  { name: "shield",       Icon: Shield,        category: "Tech", keywords: "security compliance auth" },

  // — Operations / supply chain (12)
  { name: "workflow",     Icon: Workflow,      category: "Operations", keywords: "process automation pipeline" },
  { name: "truck",        Icon: Truck,         category: "Operations", keywords: "logistics shipping supply" },
  { name: "forklift",     Icon: Forklift,      category: "Operations", keywords: "warehouse logistics ops" },
  { name: "boxes",        Icon: Boxes,         category: "Operations", keywords: "inventory warehouse stock" },
  { name: "package",      Icon: Package,       category: "Operations", keywords: "shipping order fulfillment" },
  { name: "factory",      Icon: Factory,       category: "Operations", keywords: "manufacturing plant prod" },
  { name: "fuel",         Icon: Fuel,          category: "Operations", keywords: "energy fuel station" },
  { name: "gauge",        Icon: Gauge,         category: "Operations", keywords: "metric kpi performance" },
  { name: "anchor",       Icon: Anchor,        category: "Operations", keywords: "ports shipping marine" },
  { name: "map-pin",      Icon: MapPin,        category: "Operations", keywords: "location pin map" },
  { name: "route",        Icon: Route,         category: "Operations", keywords: "routing path navigation" },
  { name: "clipboard-ok", Icon: ClipboardCheck,category: "Operations", keywords: "qa quality compliance" },

  // — Sales / CRM (11)
  { name: "target",       Icon: Target,        category: "Sales", keywords: "sales goals targeting" },
  { name: "handshake",    Icon: Handshake,     category: "Sales", keywords: "sales partnership deal" },
  { name: "user-check",   Icon: UserCheck,     category: "Sales", keywords: "lead qualified prospect" },
  { name: "presentation", Icon: Presentation,  category: "Sales", keywords: "pitch deck demo" },
  { name: "shopping-cart",Icon: ShoppingCart,  category: "Sales", keywords: "cart ecommerce checkout" },
  { name: "shopping-bag", Icon: ShoppingBag,   category: "Sales", keywords: "shopping order retail" },
  { name: "tag",          Icon: Tag,           category: "Sales", keywords: "tag price label promo" },
  { name: "discount",     Icon: Percent,       category: "Sales", keywords: "discount percent offer" },
  { name: "gift",         Icon: Gift,          category: "Sales", keywords: "gift loyalty rewards" },
  { name: "trophy",       Icon: Trophy,        category: "Sales", keywords: "win achievement quota" },
  { name: "star-sales",   Icon: Star,          category: "Sales", keywords: "favorite featured top" },

  // — HR / people (12)
  { name: "users",        Icon: Users,         category: "People", keywords: "team people group" },
  { name: "users-round",  Icon: UsersRound,    category: "People", keywords: "team round people" },
  { name: "user-plus",    Icon: UserPlus,      category: "People", keywords: "onboarding hiring new" },
  { name: "user-cog",     Icon: UserCog,       category: "People", keywords: "hr admin people" },
  { name: "user-search",  Icon: UserSearch,    category: "People", keywords: "recruiting search candidate" },
  { name: "graduation",   Icon: GraduationCap, category: "People", keywords: "training learning education" },
  { name: "award",        Icon: Award,         category: "People", keywords: "recognition achievement" },
  { name: "badge-check",  Icon: BadgeCheck,    category: "People", keywords: "verified approved trust" },
  { name: "id-card",      Icon: IdCard,        category: "People", keywords: "identity badge employee" },
  { name: "contact",      Icon: Contact,       category: "People", keywords: "contact person profile" },
  { name: "smile",        Icon: Smile,         category: "People", keywords: "happy positive csat" },
  { name: "heart-shake",  Icon: HeartHandshake,category: "People", keywords: "empathy care wellbeing" },

  // — Project management (12)
  { name: "kanban",       Icon: Kanban,        category: "Project", keywords: "project board agile" },
  { name: "list-checks",  Icon: ListChecks,    category: "Project", keywords: "tasks todo project" },
  { name: "list-todo",    Icon: ListTodo,      category: "Project", keywords: "todo task list" },
  { name: "calendar",     Icon: CalendarClock, category: "Project", keywords: "schedule reminder meeting" },
  { name: "calendar-d",   Icon: CalendarDays,  category: "Project", keywords: "calendar dates planning" },
  { name: "timer",        Icon: Timer,         category: "Project", keywords: "timer pomodoro track" },
  { name: "hourglass",    Icon: Hourglass,     category: "Project", keywords: "time waiting deadline" },
  { name: "milestone",    Icon: Milestone,     category: "Project", keywords: "milestone progress release" },
  { name: "flag",         Icon: Flag,          category: "Project", keywords: "milestone status priority" },
  { name: "check",        Icon: CheckCircle2,  category: "Project", keywords: "done complete check" },
  { name: "gantt",        Icon: GanttChart,    category: "Project", keywords: "gantt schedule plan" },
  { name: "target-pm",    Icon: TargetPM,      category: "Project", keywords: "goal okr objective" },

  // — Data / analytics (10)
  { name: "pie-chart",    Icon: PieChart,      category: "Data", keywords: "analytics distribution share" },
  { name: "table",        Icon: Table,         category: "Data", keywords: "table sql rows query" },
  { name: "search",       Icon: Search,        category: "Data", keywords: "search query find" },
  { name: "filter",       Icon: Filter,        category: "Data", keywords: "filter sift narrow" },
  { name: "scan-text",    Icon: ScanText,      category: "Data", keywords: "ocr text extraction" },
  { name: "telescope",    Icon: Telescope,     category: "Data", keywords: "discovery search lookout" },
  { name: "file-bar",     Icon: FileBarChart,  category: "Data", keywords: "report dashboard analytics" },
  { name: "layers-data",  Icon: LayersData,    category: "Data", keywords: "stack tier dimension" },
  { name: "function",     Icon: FunctionSquare,category: "Data", keywords: "formula function compute" },
  { name: "sparkles",     Icon: Sparkles,      category: "Data", keywords: "ai magic generative" },

  // — AI / automation (10)
  { name: "bot",          Icon: Bot,           category: "AI", keywords: "robot assistant agent" },
  { name: "wand",         Icon: Wand2,         category: "AI", keywords: "magic generate transform" },
  { name: "atom",         Icon: Atom,          category: "AI", keywords: "atom physics quantum" },
  { name: "cog",          Icon: Cog,           category: "AI", keywords: "engine machinery automation" },
  { name: "zap",          Icon: Zap,           category: "AI", keywords: "automation fast lightning" },
  { name: "lightbulb",    Icon: Lightbulb,     category: "AI", keywords: "idea insight innovation" },
  { name: "rocket",       Icon: Rocket,        category: "AI", keywords: "launch boost go fast" },
  { name: "orbit",        Icon: Orbit,         category: "AI", keywords: "orbit satellite planet" },
  { name: "infinity",     Icon: InfinityIcon,  category: "AI", keywords: "infinity loop continuous" },
  { name: "flame",        Icon: Flame,         category: "AI", keywords: "trending hot fire" },

  // — Geography / nature (8)
  { name: "globe",        Icon: Globe,         category: "World", keywords: "global international web" },
  { name: "compass",      Icon: Compass,       category: "World", keywords: "navigation discovery direction" },
  { name: "mountain",     Icon: Mountain,      category: "World", keywords: "mountain peak hike" },
  { name: "tree",         Icon: TreePine,      category: "World", keywords: "tree environment nature" },
  { name: "sunrise",      Icon: Sunrise,       category: "World", keywords: "morning sunrise dawn" },
  { name: "moon",         Icon: Moon,          category: "World", keywords: "night moon dark" },
  { name: "cloud-g",      Icon: CloudG,        category: "World", keywords: "weather forecast sky" },
  { name: "plane",        Icon: Plane,         category: "World", keywords: "travel airline flight" },

  // — Misc / personality (10)
  { name: "crown",        Icon: Crown,         category: "Style", keywords: "premium royal vip top" },
  { name: "diamond",      Icon: Diamond,       category: "Style", keywords: "premium gem jewel" },
  { name: "gem",          Icon: Gem,           category: "Style", keywords: "premium stone treasure" },
  { name: "shapes",       Icon: Shapes,        category: "Style", keywords: "shapes geometry design" },
  { name: "palette",      Icon: Palette,       category: "Style", keywords: "color art design" },
  { name: "paintbrush",   Icon: Paintbrush,    category: "Style", keywords: "design art creative" },
  { name: "music",        Icon: Music,         category: "Style", keywords: "music audio sound" },
  { name: "camera",       Icon: Camera,        category: "Style", keywords: "photo image camera" },
  { name: "video",        Icon: Video,         category: "Style", keywords: "video record media" },
  { name: "image",        Icon: ImageIcon,     category: "Style", keywords: "image photo media" },
];

export const AGENT_ICON_BY_NAME = Object.fromEntries(
  AGENT_ICON_LIBRARY.map(e => [e.name, e]),
);

// Ordered list of categories present in the library — preserves the
// authoring order above so the picker reads naturally.
export const AGENT_ICON_CATEGORIES = (() => {
  const seen = new Set();
  const ordered = [];
  for (const entry of AGENT_ICON_LIBRARY) {
    if (entry.category && !seen.has(entry.category)) {
      seen.add(entry.category);
      ordered.push(entry.category);
    }
  }
  return ordered;
})();

// Group library entries by category. Used by AvatarPicker to render
// section headers. { [category: string]: AgentIconEntry[] }.
export const AGENT_ICONS_BY_CATEGORY = AGENT_ICON_CATEGORIES.reduce((acc, cat) => {
  acc[cat] = AGENT_ICON_LIBRARY.filter(e => e.category === cat);
  return acc;
}, {});


// ─── Resolution ──────────────────────────────────────────────────


// Default mapping: agent.template → suggested {color, iconName}.
// Replaces the prior "borrow nav-tab category" pattern. Used at agent
// creation time as a sensible default the admin can override via the
// AvatarPicker.
const TEMPLATE_DEFAULTS = {
  legal_contracts:      { color: "violet", iconName: "scale" },
  "legal-contracts":    { color: "violet", iconName: "scale" },
  legal:                { color: "violet", iconName: "scale" },
  it_helpdesk:          { color: "blue",   iconName: "headphones" },
  "it-helpdesk":        { color: "blue",   iconName: "headphones" },
  network_telecom:      { color: "cyan",   iconName: "network" },
  "network-telecom":    { color: "cyan",   iconName: "network" },
  healthcare:           { color: "tomato", iconName: "stethoscope" },
  finance:              { color: "emerald", iconName: "dollar" },
  "finance-accounting": { color: "emerald", iconName: "receipt" },
  finance_accounting:   { color: "emerald", iconName: "receipt" },
  supply_chain:         { color: "amber",  iconName: "truck" },
  "supply-chain":       { color: "amber",  iconName: "truck" },
  hr:                   { color: "pink",   iconName: "users" },
  "hr-people-ops":      { color: "pink",   iconName: "users" },
  hr_people_ops:        { color: "pink",   iconName: "users" },
  sales:                { color: "indigo", iconName: "target" },
  "sales-crm":          { color: "indigo", iconName: "handshake" },
  sales_crm:            { color: "indigo", iconName: "handshake" },
  project_management:   { color: "purple", iconName: "kanban" },
  "project-management": { color: "purple", iconName: "kanban" },
  engineering_devops:   { color: "slate",  iconName: "code" },
  "engineering-devops": { color: "slate",  iconName: "code" },
  code:                 { color: "slate",  iconName: "code" },
  generic:              { color: "indigo", iconName: "bot" },
};


/**
 * Resolve a {color, iconName, source} triple for an agent.
 *
 * Priority:
 *   1. agent.metadata_json.icon (admin override; takes precedence)
 *   2. TEMPLATE_DEFAULTS[agent.template]
 *   3. agent_kind fallback (supervisor → layers, otherwise → bot)
 *   4. ultimate generic: { indigo, bot }
 *
 * source distinguishes the three for downstream UI hints (e.g. a
 * "Customize" affordance on auto-derived avatars but not overrides).
 */
export function resolveAgentVisual(agent) {
  if (!agent) return { color: "indigo", iconName: "bot", source: "fallback" };

  const override = agent.metadata_json?.icon;
  if (override && typeof override === "object" && override.color && override.iconName) {
    if (AGENT_PALETTE[override.color] && AGENT_ICON_BY_NAME[override.iconName]) {
      return { ...override, source: "override" };
    }
  }

  const tpl = (agent.template || "").toString();
  if (TEMPLATE_DEFAULTS[tpl]) {
    return { ...TEMPLATE_DEFAULTS[tpl], source: "template" };
  }

  if (agent.agent_kind === "supervisor") {
    return { color: "purple", iconName: "shapes", source: "kind" };
  }

  // Use-case experts (regulars stamped as part of a supervisor family)
  // get a visually distinct default — teal + atom — so when they
  // render in the supervisor's Overview tile grid they read as
  // "specialist sub-units of the family" rather than collapsing
  // visually with the supervisor's purple+shapes mark or with
  // standalone regular agents elsewhere. Template/override paths
  // above still win, so a domain-templated expert keeps its domain
  // palette.
  if (agent.metadata_json?.is_use_case_expert === true) {
    return { color: "teal", iconName: "atom", source: "expert" };
  }

  return { color: "indigo", iconName: "bot", source: "fallback" };
}


// ─── Category helper ─────────────────────────────────────────────


// Effective category for an agent — explicit `metadata_json.category`
// wins; otherwise platform-managed agents (Super Agent / Platform
// Tester / Orchestrator) fall back to "System" so they group together
// on the Library category strip without an admin having to type one in.
//
// Mirrors the FLAGS list in viewer/edit/platformManagedAgent.js so the
// two sources stay in sync.
const PLATFORM_MANAGED_FLAGS = [
  "is_super_agent",
  "is_platform_tester",
  "is_self_assembling",
];

export function getEffectiveCategory(agent) {
  const explicit = (agent?.metadata_json?.category || "").trim();
  if (explicit) return explicit;
  const meta = agent?.metadata_json || {};
  if (PLATFORM_MANAGED_FLAGS.some(f => meta[f] === true)) return "System";
  return "";
}


// ─── Initials helper for avatar fallback ─────────────────────────


export function agentInitials(agent) {
  const name = (agent?.name || "").trim();
  if (!name) return "AI";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
